import { generateId } from "shared/functions";

enum GifPauserMode {
	ANIMATING,
	HOVER_ONLY,
	OFF
}

export class GifPauserElement {

	private modeValue: GifPauserMode = GifPauserMode.ANIMATING;
	private readonly source: string;
	private dataURIPromise?: Promise<string>;
	private hovered: boolean = false;

	constructor(
		private element: HTMLImageElement
	) {
		this.source = this.element.src;
		this.element.addEventListener("mouseover", () => {
			if (!this.hovered && (this.modeValue !== GifPauserMode.OFF)) {
				this.unpause();
			}
			this.hovered = true;
		});
		this.element.addEventListener("mouseout", () => {
			if (this.hovered && (this.modeValue !== GifPauserMode.ANIMATING)) {
				this.pause();
			}
			this.hovered = false;
		});
	}

	set mode(value: GifPauserMode) {
		this.modeValue = value;
		if ((value === GifPauserMode.HOVER_ONLY && this.hovered) || value === GifPauserMode.ANIMATING) {
			this.unpause();
		} else {
			this.pause();
		}
	}

	get mode(): GifPauserMode {
		return this.modeValue;
	}

	async getDataURI(): Promise<string> {
		if (this.dataURIPromise) {
			return this.dataURIPromise;
		}

		return this.dataURIPromise = new Promise((resolve) => {
			const image: HTMLImageElement = document.createElement("img");
			const canvas: HTMLCanvasElement = document.createElement("canvas");
			const context = canvas.getContext("2d");
			image.setAttribute("crossOrigin", "anonymous");
			if (!context) {
				throw new Error("Could not get context from canvas");
			}

			image.onload = () => {
				canvas.width = this.element.naturalWidth;
				canvas.height = this.element.naturalHeight;
				context.drawImage(image, 0, 0);
				resolve(canvas.toDataURL());
			};
			image.src = this.element.src + (this.element.src.indexOf("?") === -1 ? "?" : "&") + "gif-pauser-nocache";
		});
	}

	private pause(): void {
		this.getDataURI().then(URI => this.element.src = URI);
	}

	private unpause(): void {
		this.element.src = this.source;
	}
}

const WCAG_ANIMATION_TTL = 5000;
export class GifPauser {
	private elements: Record<string, GifPauserElement> = { };
	private isPaused: boolean = false;
	private reducedMotionQuery = window.matchMedia("(prefers-reduced-motion)");

	constructor(private gifTTL: number = (WCAG_ANIMATION_TTL - 500)) {
		this.update();
		this.reducedMotionQuery.onchange = () => {
			const mode = this.reducedMotionQuery.matches ? GifPauserMode.OFF : (this.paused ? GifPauserMode.HOVER_ONLY : GifPauserMode.ANIMATING);
			Object.entries(this.elements).forEach(([, gif]) => {
				gif.mode = mode;
			});
		};
	}

	private makeElement(image: HTMLImageElement): GifPauserElement {
		const element = new GifPauserElement(image);
		if (this.reducedMotionQuery.matches) {
			element.mode = GifPauserMode.OFF;
		} else {
			setTimeout(() => {
				element.mode = GifPauserMode.HOVER_ONLY;
			}, this.gifTTL);
			element.mode = GifPauserMode.ANIMATING;
		}
		return element;
	}

	public update(): void {
		Array.from(document.getElementsByTagName("img"))
			.filter(i => i.src.match(/\.gif/))
			.forEach(image => {
				if (!image.id) {
					image.id = generateId("gif-pause-");
				}
				if (!this.elements.hasOwnProperty(image.id)) {
					this.elements[image.id] = this.makeElement(image);
				}
			});
	}

	get paused(): boolean {
		return this.isPaused;
	}

	set paused(paused: boolean) {
		this.update();
		Object.values(this.elements).forEach(element => element.mode = paused ? GifPauserMode.HOVER_ONLY : GifPauserMode.ANIMATING);
		this.isPaused = true;
	}
}
